import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { API_BASE, page, cardScroll, DirectoryTabs, inp, btn, btnDisabled, navigateUnlessTextSelected } from './AdminUsers';
import TopBanner from '../components/TopBanner';
import AdminSidebar from '../components/AdminSidebar';

// "User Groups" tab (2026-08-05) — lists every real (non-system) Authentik
// group so an admin can browse into one and manage its members.
// authentik Read-only / authentik Admins / bescom-hierarchy-store are
// excluded server-side (adminGroups.js) — none of these are groups a
// BESCOM admin should be assigning real employees into from this panel.

const th = {
  textAlign: 'left', padding: '10px 12px', fontSize: '12px', fontWeight: '700',
  color: '#5F5E5A', borderBottom: '2px solid #eee', whiteSpace: 'nowrap',
};
const td = {
  padding: '10px 12px', fontSize: '13px', borderBottom: '1px solid #f0f0f0',
};

export default function AdminGroups() {
  const navigate = useNavigate();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [newGroupName, setNewGroupName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  // Phase 2 of the httpOnly-cookie migration (see AuthContext.js's own
  // header comment) — no more synchronous "is there a token" pre-check
  // possible; this fetch's own 401/403 handling already covers both
  // "not logged in" and "not authorized", same as before.
  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/admin/groups`, {
          credentials: 'include',
        });
        if (res.status === 403) {
          setError('You are not authorized to access this page.');
          return;
        }
        if (res.status === 401) {
          navigate('/', { state: { sessionExpired: true } });
          return;
        }
        const data = await res.json();
        setGroups(data.results || []);
      } catch (e) {
        setError('Could not reach the identity service.');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  async function handleCreateGroup(e) {
    e.preventDefault();
    const name = newGroupName.trim();
    if (!name) return;
    setCreating(true);
    setCreateError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/groups`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (res.status === 401) {
        navigate('/', { state: { sessionExpired: true } });
        return;
      }
      const data = await res.json();
      if (!res.ok) {
        setCreateError(data.error || 'Could not create group.');
        return;
      }
      setGroups((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewGroupName('');
    } catch (e) {
      setCreateError('Could not reach the identity service.');
    } finally {
      setCreating(false);
    }
  }

  return (
    <>
      <TopBanner />
      <div className="admin-page-wrap" style={page}>
      <div className="admin-row" style={{ display: 'flex', gap: '24px', width: '100%', height: '100%' }}>
      <AdminSidebar active="users" />
      <div style={cardScroll}>
        <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 4px' }}>
          User Directory
        </h1>
        <p style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
          Click a group to view and manage its members.
        </p>

        <DirectoryTabs active="groups" />

        <form onSubmit={handleCreateGroup} style={{ display: 'flex', gap: '10px', margin: '16px 0' }}>
          <input
            style={{ ...inp, maxWidth: '260px' }}
            type="text"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            placeholder="New group name"
            disabled={creating}
          />
          <button type="submit" disabled={creating || !newGroupName.trim()} style={(creating || !newGroupName.trim()) ? btnDisabled : { ...btn, width: 'auto' }}>
            {creating ? 'Creating…' : 'Create Group'}
          </button>
        </form>
        {createError && (
          <div style={{ background: '#FDECEC', color: '#B3261E', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' }}>
            {createError}
          </div>
        )}

        {error && (
          <div style={{ background: '#FDECEC', color: '#B3261E', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' }}>
            {error}
          </div>
        )}

        <div style={{ border: '1px solid #eee', borderRadius: '10px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                <th style={th}>Group</th>
                <th style={{ ...th, width: '140px' }}>Members</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => {
                const groupPath = `/admin/groups/${g.pk}`;
                return (
                  <tr
                    key={g.pk}
                    onClick={navigateUnlessTextSelected(navigate, groupPath)}
                    className="admin-row-hover"
                    style={{ cursor: 'pointer', background: '#fff' }}
                  >
                    {/* Only the group name is a real link — see
                        AdminUsers.jsx's own DirectoryTable comment on why
                        the whole row isn't wrapped in links anymore. */}
                    <td style={{ ...td, fontWeight: '600' }} onClick={(e) => e.stopPropagation()}>
                      <Link to={groupPath}>{g.name}</Link>
                    </td>
                    <td style={td}>{g.memberCount}</td>
                  </tr>
                );
              })}
              {!loading && groups.length === 0 && (
                <tr>
                  <td style={{ ...td, color: '#666' }} colSpan={2}>No groups found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {loading && <p style={{ fontSize: '13px', color: '#666', marginTop: '12px' }}>Loading…</p>}
      </div>
      </div>
      </div>
    </>
  );
}
